import {
  Box,
  Button,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BoxHeader from "components/elements/BoxHeader";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useState, useEffect, useCallback } from "react";
import FieldsTable from "../../FieldsTable";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { amplendLoanCalculations } from "utils/amplend-loan-calculations";
import { IconAlert } from "components/widgets/IconAlert";
import { FieldValueMap } from "utils/types";
import { getErrorMessage } from "utils/errorHandler";
import { SubmitApplicationButton } from "../SubmitApplicationButton";
import { useLoanSection } from "hooks/useSections";
import { SectionModel } from "providers/loans/loan-section.model";
import { LoanFieldModel } from "providers/loans/loan-field.model";
import { LoanStatusModel } from "providers/loans/loan-status.model";
import { useQueryClient } from "@tanstack/react-query";
import InputSearch from "components/elements/InputSearch/input-search";
import { useLoanActions } from "providers/loans/mutations/loan-actions";
import { LoanApplicationToogle, LoanApplicationToogleOptions } from "../loan-application-toogle";

interface SectionWithFields extends SectionModel {
  fields: LoanFieldModel[];
}

// MENU > LOANS > LOAN DETAILS
const SectionFieldsView = ({
  isOpen,
  setEditing,
}: {
  isOpen: boolean;
  setEditing: (editing: boolean) => void;
}) => {
  const { user } = useTenant();
  const { loan, refetch: reloadLoan } = useContext(LoanContext);
  const { showError, showSuccess } = useContext(AlertsContext);
  const { data: loanSections } = useLoanSection();
  const queryClient = useQueryClient();

  const applicationTerm =
    user?.lender_company?.id === "2e6eae2b-de55-4c49-96c6-ff77740565f1" ||
    user?.lender_company?.id === "59d63802-e33d-4b99-9ddd-e92004455eb3"
      ? "Term Sheet"
      : "Application";

  const [, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [canNotEditLoan, setCanNotEditLoan] = useState(false);
  const [sections, setSections] = useState<SectionWithFields[]>([]);
  const [sectionsOnView, setSectionsOnView] = useState<SectionWithFields[]>([]);
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const [values, setValues] = useState<FieldValueMap>({});
  const [activeTab, setActiveTab] = useState<LoanApplicationToogleOptions>();

  const { applyChanges } = useLoanActions();

  function enableEditMode(value: boolean) {
    setEditing(value);
  }

  function showEditButton() {
    if (user.type === "lender") {
      return true;
    }

    if (loan?.status.isDraft) {
      return true;
    }

    if (loan?.status.order && loan.status.order < 5) {
      return true;
    }

    return false;
  }

  const filterApplicationFields = useCallback(
    (field: any) => {
      if (user.type === "borrower") {
        return field.show_on_borrower_application;
      } else if (user.type === "lender") {
        return field.show_on_lender_application;
      }

      return false;
    },
    [user.type]
  );

  const filterLoanFields = useCallback(
    (field: any) => {
      if (user.type === "borrower") {
        return field.show_on_borrower_loan;
      } else if (user.type === "lender") {
        return field.show_on_lender_loan;
      }

      return false;
    },
    [user.type]
  );

  const applyFilter = useCallback(() => {
    if (sections.length > 0) {
      const filteredSections = sections
        .map(section => ({
          ...section,
          fields: section.fields
            .filter(field =>
              activeTab === LoanApplicationToogleOptions.Application
                ? filterApplicationFields(field)
                : filterLoanFields(field)
            )
            .filter(
              field => !filterText || field.label.toLowerCase().includes(filterText.toLowerCase())
            ),
        }))
        // Remove empty sections
        .filter(section => section.fields.length > 0);

      setSectionsOnView(filteredSections.sort((a, b) => a.order - b.order));
    }
  }, [activeTab, filterApplicationFields, filterLoanFields, filterText, sections]);

  useEffect(() => {
    if (sections.length > 0) {
      applyFilter();
      // Ensure all sections are open by default
      setOpenIndexes(Array.from({ length: sections.length }, (_, i) => i));
    }
  }, [sections, applyFilter]);

  useEffect(() => {
    if (loan && loanSections) {
      // Log fields grouped by section ID
      const fieldsBySectionId: Record<string, LoanFieldModel[]> = {};
      loan.fields.forEach(field => {
        const sectionId = field.section?.id;
        if (sectionId) {
          if (!fieldsBySectionId[sectionId]) {
            fieldsBySectionId[sectionId] = [];
          }
          fieldsBySectionId[sectionId].push(field);
        }
      });

      const sections: SectionWithFields[] = loanSections
        .map(section => ({
          ...section,
          fields: loan!.fields.filter(field => field.section?.id === section.id) ?? [],
        }))
        // Ensure sections are sorted by order
        .sort((a, b) => a.order - b.order);

      setSections(sections);

      // Ensure all sections are open by default
      const newOpenIndexes = Array.from({ length: sections.length }, (_, i) => i);
      setOpenIndexes(newOpenIndexes);

      const loanValues = loan!.fields
        .filter(field => loan!.values?.find(value => value.loan_field.id === field.id))
        .map(field => {
          const fieldValue = loan!.values?.find(value => value.loan_field.id === field.id);

          return {
            id: fieldValue?.id?.toString() ?? "",
            loan: loan.id,
            value: { field: fieldValue?.value?.field },
            loan_field: { id: field.id },
          };
        });

      const calculatedFields = amplendLoanCalculations({
        loanValues: loanValues,
        loanFields: loan!.fields.map(field => ({
          id: field.id,
          label: field.label,
          identifier_key: field.identifier_key ?? "",
        })),
        propertyFields: loan!.property?.property_detail_fields ?? [],
        propertyValues:
          loan!.property?.values.map(value => ({
            id: value.id.toString(),
            value: { field: value.value.field },
            property_field: value.property_field,
          })) ?? [],
        loan: loan!,
      });

      setValues(calculatedFields);

      if (loan.status.isSubmitted) {
        setActiveTab(LoanApplicationToogleOptions.Loan);
      } else {
        setActiveTab(LoanApplicationToogleOptions.Application);
      }

      if (isPermissionEnabled(user) && hasPermission(user, permissionConstants.readOnlyLoans)) {
        setCanNotEditLoan(true);
      } else {
        if (user.type === "borrower") {
          if (loan.status.isSubmitted) {
            setCanNotEditLoan(true);
          }
        }
      }
    }
  }, [loan, loanSections, user]);

  const handleAccordionChange = (indexes: number[]) => {
    setOpenIndexes(indexes);
  };

  async function handleBeforeSubmit(): Promise<boolean> {
    try {
      setLoading(true);

      const statuses: LoanStatusModel[] = user.lender_company?.statuses ?? user.company.statuses;

      const submitted_status = statuses
        .sort((a, b) => a.order - b.order)
        .find(status => status.isSubmitted);

      if (!submitted_status) {
        throw new Error("No submitted status found");
      }

      await applyChanges.mutateAsync({
        id: loan!.id,
        status: submitted_status.id,
      });

      showSuccess({
        title: "Loan Submitted",
        description: "The loan has been submitted for review.",
      });

      queryClient.invalidateQueries({ queryKey: ["loanSection"] });

      return true;
    } catch (err) {
      showError({
        title: "Loan Not Submitted",
        description: getErrorMessage(err),
      });
      return false;
    } finally {
      setLoading(false);
    }
  }

  // Function to generate a unique data key for cloned fields
  const generateUniqueDataKey = (
    originalKey: string | null,
    sectionNumber: number
  ): string | null => {
    if (originalKey === null || originalKey === undefined) return null;

    // Check if the key already has a number suffix
    const keyMatch = originalKey.match(/^(.*?)(_(\d+))?$/);
    if (keyMatch) {
      const baseKey = keyMatch[1];
      return `${baseKey}_${sectionNumber}`;
    }

    return `${originalKey}_${sectionNumber}`;
  };

  // Function to clone a section
  const handleCloneSection = async (sectionId: string, sectionName: string) => {
    try {
      setLoading(true);

      // Find the original section
      const originalSection = sections.find(section => section.id === sectionId);

      if (!originalSection) {
        throw new Error("Original section not found");
      }

      // Find all sections with similar names to determine the next number
      const similarSections = sections.filter(section =>
        section.section_name.startsWith("Individual Information")
      );

      // Find the highest number used in similar section names
      let highestNumber = 1; // Start with 1 so the first clone will be "Individual Information 2"
      similarSections.forEach(section => {
        const match = section.section_name.match(/^(.*?)(\s+(\d+))?$/);
        if (match && match[3]) {
          const num = parseInt(match[3]);
          if (num > highestNumber) {
            highestNumber = num;
          }
        }
      });

      // Create a new section name with the next number
      const nextNumber = highestNumber + 1;
      const newSectionName = `Individual Information ${nextNumber}`;

      // Insert the new section
      const { loan_section } = await apiRequest("/supabase/loan_section/insert", {
        name: newSectionName,
      });

      if (!loan_section) {
        throw new Error("Failed to create cloned section");
      }

      if (!originalSection.fields.length) {
        showSuccess({
          title: "Section Cloned",
          description: "Section was successfully cloned but has no fields to copy.",
        });
        reloadLoan();
        queryClient.invalidateQueries({ queryKey: ["loanSection"] });
        return;
      }

      // Clone each field to the new section
      const fieldPromises = originalSection.fields.map(field =>
        apiRequest("/supabase/loan_fields/insert", {
          field: {
            label: field.label,
            type: field.type,
            data_key: generateUniqueDataKey(field.data_key, nextNumber),
            required: field.required,
            options: field.options,
            placeholder: field.placeholder,
            sectionId: loan_section.id,
            show_on_borrower_application: field.show_on_borrower_application,
            show_on_lender_application: field.show_on_lender_application,
            show_on_borrower_loan: field.show_on_borrower_loan,
            show_on_lender_loan: field.show_on_lender_loan,
            identifier_key: generateUniqueDataKey(field.identifier_key, nextNumber),
            editable: field.editable,
            underwriting: field.underwriting,
            calculated: field.calculated,
            do_not_allow_borrower_to_edit: field.do_not_allow_borrower_to_edit,
            order: field.order,
          },
        })
      );

      await Promise.all(fieldPromises);

      showSuccess({
        title: "Section Cloned",
        description: `"${sectionName}" was successfully cloned to "${newSectionName}".`,
      });

      // Invalidate all relevant queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["loanSection"] });
      queryClient.invalidateQueries({
        queryKey: ["/supabase/loans/single"],
      });

      // Reload the loan to get the updated sections
      await reloadLoan();

      // Force a re-render by updating the openIndexes to include all sections
      setTimeout(() => {
        // Set all sections to be open
        const newOpenIndexes = Array.from({ length: sections.length + 1 }, (_, i) => i);
        setOpenIndexes(newOpenIndexes);
      }, 500);
    } catch (err) {
      showError({
        title: "Failed to Clone Section",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to handle removing a cloned section
  const handleRemoveSection = async (sectionId: string, sectionName: string) => {
    try {
      setLoading(true);

      // Confirm this is a cloned section
      const nameMatch = sectionName.match(/^(.*?)(\s+(\d+))?$/);
      if (!nameMatch || !nameMatch[3]) {
        throw new Error("Only cloned sections can be removed");
      }

      // Delete the section
      await apiRequest("/supabase/loan_section/delete", {
        sectionId: sectionId,
      });

      showSuccess({
        title: "Section Removed",
        description: `"${sectionName}" was successfully removed.`,
      });

      // Invalidate all relevant queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["loanSection"] });
      queryClient.invalidateQueries({
        queryKey: ["/supabase/loans/single"],
      });

      // Reload the loan to get the updated sections
      await reloadLoan();

      // Force a re-render by updating the sections state
      setSections(prevSections => {
        const updatedSections = prevSections.filter(section => section.id !== sectionId);
        return updatedSections;
      });

      // Update the openIndexes to match the new sections array
      setTimeout(() => {
        const newOpenIndexes = Array.from({ length: sections.length }, (_, i) => i);
        setOpenIndexes(newOpenIndexes);
      }, 500);
    } catch (err) {
      showError({
        title: "Failed to Remove Section",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const SectionFields = () => {
    const filteredSections = sectionsOnView.filter(
      section => (!section.underwriting && user.type === "borrower") || user.type === "lender"
    );

    // Sort sections based on the order property from the database
    const sortedSections = filteredSections.sort((a, b) => a.order - b.order);

    return (
      <Accordion allowMultiple index={openIndexes} onChange={handleAccordionChange}>
        {sortedSections.map(section => (
          <AccordionItem key={section.id} border="none">
            <h2>
              <AccordionButton pl="0px" pr="3">
                <Box flex="1" textAlign="left">
                  <Flex alignItems="center">
                    <BoxHeader mb="0px" title={section.section_name} />
                  </Flex>
                </Box>
                {section.section_name === "Individual Information" && user.type === "lender" && (
                  <Tooltip label="Clone this section">
                    <IconButton
                      as="span"
                      aria-label="Clone section"
                      icon={<FontAwesomeIcon icon={["far", "plus"]} />}
                      size="sm"
                      variant="ghost"
                      mr={2}
                      onClick={e => {
                        e.stopPropagation(); // Prevent accordion from toggling
                        handleCloneSection(section.id, section.section_name);
                      }}
                    />
                  </Tooltip>
                )}
                {section.section_name.includes("Individual Information") &&
                  section.section_name !== "Individual Information" &&
                  user.type === "lender" && (
                    <Tooltip label="Remove this section">
                      <IconButton
                        aria-label="Remove section"
                        icon={<FontAwesomeIcon icon={["far", "minus"]} />}
                        size="sm"
                        variant="ghost"
                        mr={2}
                        onClick={e => {
                          e.stopPropagation(); // Prevent accordion from toggling
                          handleRemoveSection(section.id, section.section_name);
                        }}
                      />
                    </Tooltip>
                  )}
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4} pl="0px" pr="0px">
              <Box>
                <Flex alignItems="center" justifyContent="space-between" wrap="wrap">
                  {!isOpen && section.underwriting && user.type === "lender" && (
                    <IconAlert
                      mb="0px"
                      text={`${section.section_name} values are not visible to borrowers.`}
                    />
                  )}
                  {!isOpen && !section.underwriting && user.type === "lender" && (
                    <IconAlert
                      mb="0px"
                      text={`${section.section_name} values are visible to borrowers.`}
                    />
                  )}
                </Flex>
                {isOpen && section.underwriting && user.type === "lender" && (
                  <IconAlert
                    text={`${section.section_name} values are not visible to borrowers.`}
                  />
                )}
                {isOpen && !section.underwriting && user.type === "lender" && (
                  <IconAlert text={`${section.section_name} values are visible to borrowers.`} />
                )}
                <FieldsTable
                  providedValues={values}
                  columns={isOpen ? 2 : 3}
                  fields={section.fields.filter((field: any) =>
                    field.label.toLowerCase().includes(filterText.toLowerCase())
                  )}
                />
              </Box>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  return (
    <Flex flexDirection="column" gap="6">
      <Box display="none">
        <LoanApplicationToogle
          activeTab={activeTab}
          onChange={setActiveTab}
          applicationTerm={applicationTerm}
        />
      </Box>
      <Box>
        <Flex alignItems="center" gap="2" mb="20px">
          {user.type === "lender" && !isOpen && <IconAlert mb="0px" text="Visible to borrowers." />}

          <InputSearch
            size="sm"
            value={filterText}
            onChange={ev => setFilterText(ev.target.value)}
            placeholder="Filter fields..."
            debounceTime={0}
          />

          {showEditButton() && (
            <Box>
              <Button
                variant="white"
                size="sm"
                isDisabled={canNotEditLoan}
                onClick={() => enableEditMode(true)}
              >
                Edit Details
              </Button>
            </Box>
          )}

          {!loan!.status.isSubmitted && (
            <Box>
              <SubmitApplicationButton onLoading={setLoading} beforeSubmit={handleBeforeSubmit} />
            </Box>
          )}
        </Flex>
      </Box>
      <SectionFields />
    </Flex>
  );
};

export default SectionFieldsView;
